<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <h6 class="mb-0 me-auto">Pagos de Trabajos</h6>
              <div class="d-flex align-items-center gap-2">
                <button
                  v-if="task && task.status !== 'paid'"
                  type="button"
                  class="btn btn-sm btn-success"
                  @click="openAddPaymentModal"
                >
                  <i class="fas fa-plus me-1"></i> Agregar Pago
                </button>
                <button type="button" class="btn btn-sm btn-secondary" @click="goBack">
                  <i class="fas fa-arrow-left me-1"></i> Volver
                </button>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <!-- Información del trabajo/deuda -->
            <div v-if="task" class="px-4 pt-3 pb-2 border-bottom">
              <div class="row">
                <div class="col-md-6">
                  <p class="mb-1"><strong>Operario:</strong> {{ task.user?.name || 'N/A' }}</p>
                  <p class="mb-1"><strong>Descripción:</strong> {{ task.description }}</p>
                  <p class="mb-1"><strong>Tipo:</strong> 
                    <span class="badge badge-sm" :class="{
                      'bg-gradient-info': task.type === 'task',
                      'bg-gradient-warning': task.type === 'debt'
                    }">
                      {{ task.type === 'task' ? 'Tarea' : 'Deuda' }}
                    </span>
                  </p>
                </div>
                <div class="col-md-6">
                  <p class="mb-1"><strong>Precio Total:</strong> 
                    {{ parseFloat(task.price ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </p>
                  <p class="mb-1"><strong>Ganancia empleado ({{ employeeCommissionPercent }}%):</strong>
                    {{ employeeEarning.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </p>
                  <p class="mb-1"><strong>Pagado al empleado:</strong> 
                    <span class="text-success">
                      {{ parseFloat(task.employee_paid ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                    </span>
                  </p>
                  <p class="mb-1"><strong>Pendiente al empleado:</strong>
                    <span class="text-warning">
                      {{ employeeBalance.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                    </span>
                  </p>
                  <p class="mb-1"><strong>Total Pagado (trabajo):</strong> 
                    <span class="text-success">
                      {{ parseFloat(task.total_paid ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                    </span>
                  </p>
                  <p class="mb-1"><strong>Saldo Pendiente (trabajo):</strong> 
                    <span class="text-danger">
                      {{ parseFloat((task.price ?? 0) - (task.total_paid ?? 0)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                    </span>
                  </p>
                  <p class="mb-1"><strong>Estado:</strong> 
                    <span class="badge badge-sm" :class="{
                      'bg-gradient-warning': task.status === 'pending',
                      'bg-gradient-info': task.status === 'partial',
                      'bg-gradient-success': task.status === 'paid'
                    }">
                      {{
                        task.status === 'pending' ? 'Pendiente' :
                        task.status === 'partial' ? 'Parcial' :
                        task.status === 'paid' ? 'Pagado' : ''
                      }}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Tabla de pagos (historial) -->
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">#</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descripción</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Monto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(payment, index) in paymentHistory" :key="index">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <h6 class="mb-0 text-sm">{{ index + 1 }}</h6>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <p class="text-xs mb-0">{{ payment.description || 'Pago registrado' }}</p>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm text-success">
                        {{ parseFloat(payment.amount ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm">
                        {{ formatDateOnly(payment.date) || 'Sin fecha' }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span class="badge badge-sm bg-gradient-success">Pagado</span>
                    </td>
                  </tr>
                  <tr v-if="!paymentHistory || paymentHistory.length === 0">
                    <td colspan="5" class="text-center text-xxs">No hay pagos registrados</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import { formatDateOnly } from '@/helpers/dateFormat';

export default {
  name: 'WorkerTaskPayments',
  props: {
    taskId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      task: null,
      paymentHistory: [],
    };
  },
  computed: {
    ...mapGetters('workerTasks', ['getData']),
    ...mapGetters('settings', ['employeeCommissionPercent', 'employeeCommissionRate']),
    employeeEarning() {
      const price = parseFloat(this.task?.price ?? 0);
      return Math.round(price * this.employeeCommissionRate * 100) / 100;
    },
    employeeBalance() {
      return Math.max(0, this.employeeEarning - parseFloat(this.task?.employee_paid ?? 0));
    },
  },
  methods: {
    formatDateOnly,
    async fetchTask() {
      try {
        const taskId = this.taskId || this.$route.params.taskId;
        if (!taskId) {
          Swal.fire('Error', 'No se especificó un trabajo/deuda', 'error');
          this.goBack();
          return;
        }
        
        const response = await this.$store.dispatch('workerTasks/getData', taskId);
        if (response && response.data) {
          this.task = response.data;
          this.buildPaymentHistory();
        }
      } catch (error) {
        console.error('Error fetching task:', error);
        Swal.fire('Error', 'No se pudo cargar la información del trabajo/deuda', 'error');
        this.goBack();
      }
    },
    buildPaymentHistory() {
      // Construir historial de pagos basado en total_paid
      // Como no hay tabla de pagos individuales, simulamos el historial
      this.paymentHistory = [];
      
      if (this.task && this.task.total_paid > 0) {
        // Si hay pagos, crear un registro simulado
        this.paymentHistory.push({
          description: 'Pago registrado',
          amount: this.task.total_paid,
          date: this.task.updated_at || this.task.created_at,
        });
      }
    },
    isValidDate(date) {
      if (!date) return false;
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    },
    openAddPaymentModal() {
      if (!this.task) return;
      
      const balance = parseFloat(this.task.price) - parseFloat(this.task.total_paid);
      
      Swal.fire({
        title: 'Agregar Pago',
        html: `
          <div style="text-align: left;">
            <p><strong>Saldo pendiente:</strong> ${balance.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <label style="display: block; margin-top: 10px; font-weight: bold;">Monto a pagar *</label>
            <input id="swal-amount" type="number" step="0.01" class="swal2-input" placeholder="Monto" style="width:100%;margin:0px;box-sizing:border-box;" max="${balance}">
            <label style="display: block; margin-top: 10px; font-weight: bold;">Descripción/Observación</label>
            <textarea id="swal-notes" class="swal2-input" placeholder="Descripción del pago (opcional)" style="width:100%;margin:0px;height:60px;box-sizing:border-box;"></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Agregar Pago',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          const amountInput = document.getElementById('swal-amount')?.value;
          const notes = document.getElementById('swal-notes')?.value?.trim() || '';
          
          const amount = parseFloat(amountInput);
          if (isNaN(amount) || amount <= 0) {
            Swal.showValidationMessage('El monto debe ser un número válido y positivo');
            return false;
          }
          if (amount > balance) {
            Swal.showValidationMessage(`El monto no puede ser mayor al saldo pendiente (${balance.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`);
            return false;
          }
          
          return { amount, notes };
        },
      }).then(async (result) => {
        if (result.isConfirmed && result.value) {
          try {
            const response = await this.$store.dispatch('workerTasks/addPayment', { 
              id: this.task.id, 
              amount: result.value.amount 
            });
            
            if (response && response.status >= 200 && response.status < 300) {
              Swal.fire('Éxito', 'Pago agregado correctamente', 'success');
              await this.fetchTask();
            } else {
              const errorMsg = response?.data?.message || 'Error al agregar el pago';
              throw new Error(errorMsg);
            }
          } catch (error) {
            console.error('Error adding payment:', error);
            const errorMsg = error.response?.data?.message || error.message || 'No se pudo agregar el pago';
            Swal.fire('Error', errorMsg, 'error');
          }
        }
      });
    },
    goBack() {
      this.$router.push({ name: 'Worker Tasks' });
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('settings/get');
    } catch (error) {
      console.error('Error loading settings for worker task payments:', error);
    }
    this.fetchTask();
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
